import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { t } from '../../game/i18n';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Sub = 'intro' | 'runup' | 'flight' | 'between' | 'done';

interface Competitor {
  isPlayer: boolean;
  name: string;
  species: string;
  skin: string;
  nationality: string;
  /** AI baseline distance (each attempt adds noise around it). */
  distance: number;
  /** Best distance so far across the rounds. */
  best: number;
  /** Sand marker objects (destroyed/redrawn when the best improves). */
  marker?: Phaser.GameObjects.GameObject[];
}

const START_X = 160;
const RUNWAY_M = 30;
const PLAYER_ATTEMPTS = 3;

/**
 * Long Jump: each AI rival takes a SINGLE jump (one per animal), then the player takes 3
 * jumps and their best counts. Player: mash the run-up, tap SALTA before the board
 * (overstep = foul). Best jump vs the rivals' single attempts wins.
 */
export class LongJump extends DisciplineScene {
  private competitors: Competitor[] = [];
  private qi = 0;
  private playerAttempt = 1; // 1..PLAYER_ATTEMPTS (the player jumps 3 times; AI jump once)
  private attemptDist = 0;

  private activeRig?: AthleteView;
  private sub: Sub = 'intro';
  private subT = 0;
  private runupDur = 0;
  private flightDur = 0;
  private takeoffX = 0;
  private landingX = 0;
  private foul = false;

  // Player run-up physics.
  private velocity = 0;
  private runDist = 0;
  private lastSide: Side | null = null;
  private cadence = 0;
  private lastTapAt = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;
  private reach = 7;

  private W = 0;
  private H = 0;
  private groundY = 0;
  private boardX = 0;
  private uiS = 1;
  private camTarget = 0;

  private speedBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private jumpBtn!: Button;
  private tapZones!: TapZones;

  constructor() {
    super('LongJump');
  }

  create(): void {
    super.create();
    this.qi = 0;
    this.playerAttempt = 1;
    this.sub = 'intro';
    this.velocity = 0; this.runDist = 0; this.cadence = 0; this.lastSide = null;
    this.activeRig = undefined;
    this.camTarget = 0;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.18);
    this.boardX = START_X + RUNWAY_M * SPRINT.pixelsPerMeter;

    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;
    this.reach = 5 + (this.athlete.stats.power / 100) * 5;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.buildHud();
    this.fetchWorldRecord('long_jump', 'distance', (m) => this.drawGroundRecord(this.boardX + m * SPRINT.pixelsPerMeter, this.groundY, this.groundY - Math.max(70, this.H * 0.16), `${m.toFixed(2)}m`));

    // Field order: AI first (targets to chase), player last.
    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.competitors = opponents.map((o) => ({
      isPlayer: false, name: o.name, species: o.species, skin: o.skin, nationality: o.nationality, distance: o.jumpDistance, best: 0,
    }));
    this.competitors.push({ isPlayer: true, name: this.athlete.name, species: this.athlete.species, skin: this.athlete.skin, nationality: this.athlete.nationality, distance: 0, best: 0 });

    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.playerTakeoff());

    this.startCountdown();
  }

  private drawField(): void {
    const endX = this.boardX + 14 * SPRINT.pixelsPerMeter;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(PALETTE.track, 1).fillRect(0, this.groundY, this.boardX + 6, this.H - this.groundY);
    g.fillStyle(PALETTE.white, 1).fillRect(this.boardX - 6, this.groundY, 10, 22);
    g.fillStyle(PALETTE.bad, 1).fillRect(this.boardX + 4, this.groundY, 4, 22);
    g.fillStyle(0xe8c98a, 1).fillRect(this.boardX + 6, this.groundY, endX, this.H - this.groundY);
    for (let m = 1; m <= 12; m++) {
      const x = this.boardX + m * SPRINT.pixelsPerMeter;
      g.fillStyle(0xcaa463, 1).fillRect(x, this.groundY, 2, 16);
      this.add.text(x, this.groundY + 20, `${m}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#9a7b3e' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(28), color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);

    const barW = Math.min(300, this.W * 0.46);
    const topY = Math.max(50, this.H * 0.13);
    this.speedBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18 });
    this.speedBar.setVisible(false);
    this.add.text(this.W / 2, topY - 18, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(13), color: '#d3dbf2' })
      .setOrigin(0.5).setScrollFactor(0);

    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(88), color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);

    const jumpLabel = this.sys.game.device.os.desktop ? `⬆ ${t('btn.jump')} ${t('btn.space')}` : `⬆ ${t('btn.jump')}`;
    this.jumpBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), jumpLabel, () => this.playerTakeoff(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.good,
    });
    this.jumpBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);

    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.nextTurn();
        } else {
          AudioEngine.countdownTick();
        }
      });
    });
  }

  /** Advance to the next athlete (AI jump once; the player jumps PLAYER_ATTEMPTS times). */
  private nextTurn(): void {
    if (this.qi >= this.competitors.length) { this.finish(); return; }
    const comp = this.competitors[this.qi]!;
    if (comp.isPlayer) this.playerAttempt = 1;
    this.activeRig?.destroy();
    const displayH = Phaser.Math.Clamp(this.H * 0.36, 180, 340);
    this.activeRig = createAthleteView(this, START_X, this.groundY, { skin: comp.skin }, displayH);
    this.activeRig.container.setDepth(6);
    this.startAttempt();
  }

  /** Begin a jump: the player's current attempt, or the AI's single attempt. */
  private startAttempt(): void {
    const comp = this.competitors[this.qi]!;
    this.velocity = 0; this.runDist = 0; this.cadence = 0; this.lastSide = null;
    this.foul = false; this.subT = 0; this.sub = 'runup';
    if (this.activeRig) { this.activeRig.container.setAlpha(1).setAngle(0); this.placeRig(START_X); this.activeRig.setPose('idle'); }
    const rec = comp.best > 0 ? t('game.recBest', { m: comp.best.toFixed(2) }) : '';

    if (comp.isPlayer) {
      this.hud.setText(t('game.youJump', { n: this.playerAttempt, max: PLAYER_ATTEMPTS, rec }));
      this.controls.setEnabled(true);
      this.lastTapAt = this.time.now / 1000;
      this.jumpBtn.setVisible(true);
      this.speedBar.setVisible(true);
      this.tapZones.setVisible(true);
    } else {
      this.hud.setText(t('game.singleJump', { name: comp.name }));
      this.controls.setEnabled(false);
      this.speedBar.setVisible(false);
      this.jumpBtn.setVisible(false);
      this.tapZones.setVisible(false);
      this.runupDur = 1.1 + Math.random() * 0.3;
      // The AI's one jump (noise around the baseline).
      this.attemptDist = Math.max(2, comp.distance + (Math.random() * 1.8 - 0.9));
    }
  }

  private onTap(side: Side): void {
    if (this.sub !== 'runup' || !this.competitors[this.qi]?.isPlayer) return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    else if (this.cadence > SPRINT.overspinThreshold) mult *= SPRINT.overspinPenalty;
    this.velocity = Math.min(this.maxSpeed * 1.06, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  private playerTakeoff(): void {
    if (this.sub !== 'runup' || !this.competitors[this.qi]?.isPlayer || !this.activeRig) return;
    const athleteX = START_X + this.runDist * SPRINT.pixelsPerMeter;
    this.foul = athleteX > this.boardX + 8;
    const speedNorm = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    const flightM = this.foul ? 0 : speedNorm * this.reach;
    this.takeoffX = athleteX;
    this.landingX = athleteX + flightM * SPRINT.pixelsPerMeter;
    this.attemptDist = this.foul ? 0 : Math.max(0, (this.landingX - this.boardX) / SPRINT.pixelsPerMeter);
    this.beginFlight();
    this.controls.setEnabled(false);
    this.jumpBtn.setVisible(false);
    this.speedBar.setVisible(false);
    this.tapZones.setVisible(false);
    if (this.foul) this.cameras.main.flash(200, 255, 60, 60);
  }

  private beginFlight(): void {
    this.flightDur = this.foul ? 0.4 : Phaser.Math.Clamp(this.attemptDist / 7, 0.5, 1.4);
    this.subT = 0;
    this.sub = 'flight';
    this.activeRig?.setMotion(0);
    this.activeRig?.setPose(this.foul ? 'exhausted' : 'celebrate');
    AudioEngine.whoosh();
    this.vibrate(HAPTICS.goodTap);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    // Smooth camera every frame (no hard snaps between turns).
    const cam = this.cameras.main;
    cam.scrollX = Phaser.Math.Linear(cam.scrollX, this.camTarget, 0.12);
    if (this.sub === 'intro' || this.sub === 'done' || this.sub === 'between') {
      this.activeRig?.update(dt);
      return;
    }
    const comp = this.competitors[this.qi]!;

    if (this.sub === 'runup') {
      if (comp.isPlayer) this.updatePlayerRunup(dt);
      else this.updateAiRunup(dt);
      return;
    }
    if (this.sub === 'flight') {
      this.updateFlight(dt);
    }
  }

  private updatePlayerRunup(dt: number): void {
    this.acc += dt;
    let steps = 0;
    while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
      this.velocity *= SPRINT.dragPerStep;
      this.runDist += this.velocity * SIM_STEP;
      this.acc -= SIM_STEP;
      steps++;
    }
    const worldX = START_X + this.runDist * SPRINT.pixelsPerMeter;
    if (worldX > this.boardX + 3 * SPRINT.pixelsPerMeter) { this.playerTakeoff(); return; }
    this.placeRig(worldX);
    const sn = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.activeRig?.setMotion(sn);
    this.activeRig?.update(dt);
    this.speedBar.set(sn);
    this.tapZones.update(dt);
    this.followCam(worldX, 0.3);
    const toBoard = (this.boardX - worldX) / SPRINT.pixelsPerMeter;
    this.hud.setText(toBoard > 0 ? t('game.boardIn', { m: toBoard.toFixed(1) }) : t('game.jumpNow'));
    if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
  }

  private acc = 0;

  private updateAiRunup(dt: number): void {
    this.subT += dt;
    const p = Phaser.Math.Clamp(this.subT / this.runupDur, 0, 1);
    const worldX = Phaser.Math.Linear(START_X, this.boardX, p);
    this.placeRig(worldX);
    this.activeRig?.setMotion(0.85);
    this.activeRig?.update(dt);
    this.followCam(worldX, 0.3);
    if (p >= 1) {
      this.takeoffX = this.boardX;
      this.landingX = this.boardX + this.attemptDist * SPRINT.pixelsPerMeter;
      this.foul = false;
      this.beginFlight();
    }
  }

  private updateFlight(dt: number): void {
    this.subT += dt;
    const p = Phaser.Math.Clamp(this.subT / this.flightDur, 0, 1);
    const x = Phaser.Math.Linear(this.takeoffX, this.landingX, p);
    const arc = Math.sin(p * Math.PI) * (this.H * 0.16) * (this.foul ? 0.4 : 1);
    if (this.activeRig) {
      this.activeRig.container.x = x;
      this.activeRig.container.y = this.groundY - arc;
      this.activeRig.update(dt);
    }
    // Frame the board + sand pit (static) so the athlete arcs across — no ground "slide".
    this.camTarget = Phaser.Math.Clamp(this.boardX - this.W * 0.28, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) this.land();
  }

  private land(): void {
    const comp = this.competitors[this.qi]!;
    this.sub = 'between';
    this.cameras.main.shake(120, 0.004);
    AudioEngine.thud();
    this.vibrate(HAPTICS.tap);
    const jump = this.foul ? 0 : this.attemptDist;
    const isBest = jump > comp.best;
    comp.best = Math.max(comp.best, jump);
    if (isBest && jump > 0) this.placeMarker(comp, comp.best); // refresh the marker on a PB

    if (comp.isPlayer) {
      this.bigText.setText(this.foul ? t('game.foul') : `${jump.toFixed(2)} m${isBest && jump > 0 ? '  ✦' : ''}`)
        .setColor(this.foul ? '#ff5d6c' : '#ffd24a').setAlpha(1).setScale(0.6);
      this.tweens.add({ targets: this.bigText, scale: 1, duration: 400, ease: 'Back.out' });
      AudioEngine.fanfare();
    }

    if (comp.isPlayer && this.playerAttempt < PLAYER_ATTEMPTS) {
      // The player keeps the same rig for their next of three attempts.
      this.tweens.add({ targets: this.bigText, alpha: 0, delay: 900, duration: 250, onComplete: () => {
        this.playerAttempt++;
        this.startAttempt();
      } });
    } else {
      // AI take a single jump; the player is done after three — move on.
      const rig = this.activeRig;
      this.tweens.add({ targets: rig?.container, alpha: 0, delay: 900, duration: 300, onComplete: () => {
        this.qi++;
        this.nextTurn();
      } });
    }
  }

  /** Place (or refresh) the athlete's sand marker at their current best. */
  private placeMarker(comp: Competitor, distance: number): void {
    if (distance <= 0) return;
    comp.marker?.forEach((o) => o.destroy());
    const x = this.boardX + distance * SPRINT.pixelsPerMeter;
    const color = comp.isPlayer ? PALETTE.laneAccent : PALETTE.cadence;
    const poleH = Math.max(50, this.H * 0.12);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 20, x + 30, top + 10);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 5);
    const fs = Math.round(13 * this.uiS);
    const label = `${countryFlag(comp.nationality)} ${comp.isPlayer ? t('common.you').toUpperCase() : comp.name.split(' ')[0]} · ${distance.toFixed(2)}m`;
    const txt = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: comp.isPlayer ? '#ffd24a' : '#bfe6ff', padding: { x: 6, y: 3 },
    }).setOrigin(0.5, 1).setDepth(5);
    comp.marker = [g, txt];
  }

  private finish(): void {
    this.sub = 'done';
    this.controls.setEnabled(false);
    const standings: Standing[] = this.competitors
      .map((c) => ({ name: c.name, species: c.species, isPlayer: c.isPlayer, nationality: c.nationality, metric: c.best }))
      .sort((a, b) => b.metric - a.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const playerDist = this.competitors.find((c) => c.isPlayer)?.best ?? 0;
    const score = Math.round(Phaser.Math.Clamp(playerDist * 130, 0, 1000));
    const reward = getDiscipline('long_jump').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'long_jump', metricKind: 'distance',
      athleteId: this.athlete.id, athleteName: this.athlete.name, species: this.athlete.species, nationality: this.athlete.nationality,
      metric: playerDist, score, reward, rank, standings,
    };
    this.time.delayedCall(600, () => this.finishDiscipline(result));
  }

  private placeRig(worldX: number): void {
    if (this.activeRig) { this.activeRig.container.x = worldX; this.activeRig.container.y = this.groundY; }
  }

  private followCam(x: number, anchor: number): void {
    this.camTarget = Phaser.Math.Clamp(x - this.W * anchor, 0, this.cameras.main.getBounds().width - this.W);
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
